import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Lock,
  RotateCcw,
  SlidersHorizontal,
} from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { EcointelLottie, EcointelLottieMark } from '../../components/EcoIntelLoader';
import {
  DEFAULT_VIEW_CONFIG,
  VIEW_LEVEL_LABELS,
  VIEW_COMPONENTS,
  PROTECTED_KEYS,
} from '../../context/ViewComplexityContext';
import { useUserFacingError } from '../../hooks/useUserFacingError';

const LEVEL_OPTIONS = [
  { value: 'essential', label: 'Essential' },
  { value: 'standard', label: 'Standard' },
  { value: 'technical', label: 'Technical' },
  { value: 'hidden', label: 'Hidden' },
];

const GROUPS = [...new Set(VIEW_COMPONENTS.map((c) => c.group))];

export default function ViewsAdmin() {
  const { logError } = useUserFacingError();
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set(GROUPS));
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'config', 'viewConfiguration'));
        if (!cancelled && snap.exists()) {
          setConfig(snap.data());
        }
      } catch (e) {
        logError('Failed to load view config', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [logError]);

  const merged = useMemo(() => ({ ...DEFAULT_VIEW_CONFIG, ...config }), [config]);

  const handleChange = async (key, value) => {
    const next = { ...config, [key]: value };
    setConfig(next);
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'viewConfiguration'), next);
    } catch (e) {
      logError('Failed to save view config', e);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setConfig({});
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'viewConfiguration'), {});
      setShowResetConfirm(false);
    } catch (e) {
      logError('Failed to reset view config', e);
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (group) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const countByLevel = useMemo(() => {
    const counts = { essential: 0, standard: 0, technical: 0 };
    VIEW_COMPONENTS.forEach((c) => {
      const minLevel = merged[c.key] ?? DEFAULT_VIEW_CONFIG[c.key] ?? 'essential';
      if (minLevel === 'hidden') return;
      const LEVELS = ['essential', 'standard', 'technical'];
      const minIdx = LEVELS.indexOf(minLevel);
      LEVELS.forEach((lvl, idx) => {
        if (idx >= minIdx) counts[lvl]++;
      });
    });
    return counts;
  }, [merged]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <EcointelLottie message="Loading view configuration..." lottiePixelWidth={88} lottiePixelHeight={110} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            {['essential', 'standard', 'technical'].map((lvl) => (
              <div key={lvl} className="text-center">
                <div className="text-lg font-bold text-slate-800">{countByLevel[lvl]}</div>
                <div className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{VIEW_LEVEL_LABELS[lvl]}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {saving && <span className="text-xs text-slate-400 flex items-center gap-1"><EcointelLottieMark size={12} /> Saving...</span>}
            <button
              onClick={() => setShowResetConfirm(true)}
              className="px-3 py-1.5 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1.5"
            >
              <RotateCcw size={12} /> Reset to Default View
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Control which sections are visible at each detail level. Changes apply to all users immediately.
        </p>
      </div>

      {/* Reset confirmation */}
      {showResetConfirm && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <AlertCircle size={16} />
            <span>Reset all view settings to defaults? This cannot be undone.</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowResetConfirm(false)} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800">Cancel</button>
            <button onClick={handleReset} className="px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700">Reset</button>
          </div>
        </div>
      )}

      {/* Component groups */}
      {GROUPS.map((group) => {
        const items = VIEW_COMPONENTS.filter((c) => c.group === group);
        const isExpanded = expandedGroups.has(group);

        return (
          <div key={group} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <button
              onClick={() => toggleGroup(group)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
            >
              <span className="font-bold text-sm text-slate-700 flex items-center gap-2">
                <SlidersHorizontal size={14} className="text-slate-400" />
                {group}
                <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{items.length}</span>
              </span>
              {isExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>

            {isExpanded && (
              <div className="border-t border-slate-100">
                {items.map((comp, idx) => {
                  const isProtected = PROTECTED_KEYS.has(comp.key);
                  const currentValue = merged[comp.key] ?? 'essential';

                  return (
                    <div
                      key={comp.key}
                      className={`flex items-center justify-between px-5 py-3 ${idx < items.length - 1 ? 'border-b border-slate-50' : ''} ${isProtected ? 'bg-slate-50/50' : 'hover:bg-slate-50'}`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {isProtected && <Lock size={12} className="text-slate-300 shrink-0" />}
                        <span className={`text-sm ${isProtected ? 'text-slate-400' : 'text-slate-700'}`}>{comp.label}</span>
                      </div>
                      <select
                        value={currentValue}
                        onChange={(e) => handleChange(comp.key, e.target.value)}
                        disabled={isProtected}
                        className={`text-xs font-medium border rounded-lg px-2.5 py-1.5 outline-none transition-colors ${
                          isProtected
                            ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                            : currentValue === 'hidden'
                              ? 'bg-red-50 border-red-200 text-red-600'
                              : currentValue === 'technical'
                                ? 'bg-purple-50 border-purple-200 text-purple-600'
                                : currentValue === 'standard'
                                  ? 'bg-blue-50 border-blue-200 text-blue-600'
                                  : 'bg-emerald-50 border-emerald-200 text-emerald-600'
                        }`}
                      >
                        {LEVEL_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value} disabled={isProtected && opt.value !== 'essential'}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
